import { computed } from "vue";
import { useChannelStore } from "@/stores/channel.store";

/**
 * Composable for accessing channel capabilities.
 *
 * Capabilities determine which features and gating options are available
 * for the current channel:
 * - Authentication methods: otp_enabled, oauth_enabled
 * - Features: wallet_enabled, memberships_enabled
 * - Gating availability (inferred): can_use_token_gating, can_use_membership_gating, can_use_mixed_gating
 */
export function useChannelCapabilities() {
  const channelStore = useChannelStore();

  const capabilities = computed(
    () => channelStore.channel?.capabilities ?? null,
  );

  // Authentication methods
  const isOtpEnabled = computed(() => capabilities.value?.otp_enabled ?? true);
  const isOauthEnabled = computed(
    () => capabilities.value?.oauth_enabled ?? true,
  );

  // Features
  const isWalletEnabled = computed(
    () => capabilities.value?.wallet_enabled ?? false,
  );
  const isMembershipsEnabled = computed(
    () => capabilities.value?.memberships_enabled ?? false,
  );

  // Gating availability (derived from features)
  const canUseTokenGating = computed(
    () => capabilities.value?.can_use_token_gating ?? false,
  );
  const canUseMembershipGating = computed(
    () => capabilities.value?.can_use_membership_gating ?? false,
  );
  const canUseMixedGating = computed(
    () => capabilities.value?.can_use_mixed_gating ?? false,
  );
  const canUsePointsGating = computed(
    () => capabilities.value?.can_use_points_gating ?? false,
  );
  const canUseProductsGating = computed(
    () => capabilities.value?.can_use_products_gating ?? false,
  );
  const canUseAnyGating = computed(
    () =>
      canUseTokenGating.value ||
      canUseMembershipGating.value ||
      canUsePointsGating.value ||
      canUseProductsGating.value,
  );

  return {
    // Raw capabilities
    capabilities,

    // Authentication methods
    isOtpEnabled,
    isOauthEnabled,

    // Features
    isWalletEnabled,
    isMembershipsEnabled,

    // Gating availability
    canUseTokenGating,
    canUseMembershipGating,
    canUseMixedGating,
    canUsePointsGating,
    canUseProductsGating,
    canUseAnyGating,
  };
}
